import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon, Loader2Icon, CheckIcon } from "lucide-react";
import { useQuoteForm, VolumeRange, QuoteFormValues } from "../../hooks/useQuoteForm";
import { useQuote } from "../../contexts/QuoteContext";
const ease = [0.23, 1, 0.32, 1] as const;
const volumes: {
  id: VolumeRange;
  label: string;
}[] = [{
  id: "<50",
  label: "< 50 kg"
}, {
  id: "50-500",
  label: "50–500 kg"
}, {
  id: "500-5000",
  label: "0,5–5 t"
}, {
  id: ">5000",
  label: "> 5 t"
}];
const inputBase = "h-12 w-full rounded-xl border bg-white px-4 text-sm text-ink placeholder:text-stone/70 focus:outline-none focus:ring-2 focus:ring-olive/20";
export function QuoteForm() {
  const {
    values,
    errors,
    status,
    setField,
    handleSubmit,
    reset,
    items
  } = useQuoteForm();
  const {
    remove,
    open
  } = useQuote();
  const border = (k: keyof QuoteFormValues) => errors[k] ? "border-terracotta" : "border-line focus:border-olive";
  if (status === "success") {
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="rounded-2xl bg-paper p-8 md:p-12" role="status" data-id="element-209" data-mp="1yy44j4f8q6yz">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive text-paper" data-id="element-210" data-mp="28nnq4huc8exj">
          <CheckIcon className="h-6 w-6" data-id="element-211" data-mp="2dlpad63i2kal" />
        </span>
        <h3 className="mt-6 font-display text-4xl text-ink" data-id="element-212" data-mp="1fyzxg179en9y">Demande bien reçue.</h3>
        <p className="mt-4 max-w-md text-base leading-relaxed text-stone" data-id="element-213" data-mp="1kxkwvyeot2xc">
          Merci {values.name.split(" ")[0]}. Un chargé de compte vous adresse une proposition chiffrée à{" "}
          <span className="text-ink" data-id="element-214" data-mp="1pz0lg0g2prkh">{values.email}</span> sous 48 h ouvrées
          {values.samples ? ", avec l'envoi de vos échantillons" : ""}.
        </p>
        <button type="button" onClick={reset} className="mt-8 h-11 rounded-full border border-ink px-6 text-sm text-ink hover:bg-ink hover:text-paper" data-id="element-215" data-mp="31b1f9trso673">
          Nouvelle demande
        </button>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-2xl bg-paper p-6 md:p-10" data-id="element-216" data-mp="11acy9rbizejw">
      <fieldset data-id="element-217" data-mp="1f9uqyhrxzbyj">
        <legend className="text-sm font-medium text-ink" data-id="element-218" data-mp="1f8baaq2diyw8">Ingrédients à chiffrer</legend>
        <div className="mt-3 flex min-h-12 flex-wrap items-center gap-2 rounded-xl border border-dashed border-line p-2" data-id="element-219" data-mp="1b7hmrxsufm3v">
          <AnimatePresence initial={false} data-id="element-220" data-mp="niyg1mpfzhdw">
            {items.map(item => <motion.span key={item.id} layout initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease
          }} className="flex h-8 items-center gap-1 rounded-full bg-cream pl-3 pr-1 text-sm text-ink" data-id="element-221" data-mp="ijvbe3ddjd6g">
                {item.name}
                <button type="button" onClick={() => remove(item.id)} className="flex h-6 w-6 items-center justify-center rounded-full text-stone hover:bg-sand hover:text-ink" aria-label={`Retirer ${item.name}`} data-id="element-222" data-mp="3vvvxub03q05g">
                  <XIcon className="h-3.5 w-3.5" data-id="element-223" data-mp="3btrcpxre3t58" />
                </button>
              </motion.span>)}
          </AnimatePresence>
          {items.length === 0 && <span className="px-2 text-sm text-stone" data-id="element-224" data-mp="8fokcu97dx6r">
              Aucun ingrédient sélectionné —{" "}
              <a href="#catalogue" className="text-terracotta underline-offset-2 hover:underline" data-id="element-225" data-mp="ddgnylj7n7qs">
                parcourir le catalogue
              </a>{" "}
              ou décrivez votre besoin ci-dessous.
            </span>}
        </div>
        {items.length > 0 && <button type="button" onClick={open} className="mt-2 text-xs text-stone hover:text-ink" data-id="element-226" data-mp="2j5n3j29m0xxs">
            Gérer ma sélection
          </button>}
      </fieldset>

      <div className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-227" data-mp="34dnyl9etl8qo">
        <Field id="q-company" label="Société" error={errors.company} required data-id="element-228" data-mp="34f8x1a499btt">
          <input id="q-company" value={values.company} onChange={e => setField("company", e.target.value)} className={`${inputBase} ${border("company")}`} autoComplete="organization" data-id="element-229" data-mp="2zlegugnuzezh" />
        </Field>
        <Field id="q-name" label="Nom et prénom" error={errors.name} required data-id="element-230" data-mp="2pqcpde6420tq">
          <input id="q-name" value={values.name} onChange={e => setField("name", e.target.value)} className={`${inputBase} ${border("name")}`} autoComplete="name" data-id="element-231" data-mp="3naywapkszu40" />
        </Field>
        <Field id="q-email" label="E-mail professionnel" error={errors.email} required data-id="element-232" data-mp="3dd2pub2t9nc8">
          <input id="q-email" type="email" value={values.email} onChange={e => setField("email", e.target.value)} className={`${inputBase} ${border("email")}`} autoComplete="email" placeholder="prenom@societe.com" data-id="element-233" data-mp="38fsru71cw3cs" />
        </Field>
        <Field id="q-phone" label="Téléphone" data-id="element-234" data-mp="1wyay80dhok3z">
          <input id="q-phone" type="tel" value={values.phone} onChange={e => setField("phone", e.target.value)} className={`${inputBase} ${border("phone")}`} autoComplete="tel" data-id="element-235" data-mp="ci3fllqrw5l9" />
        </Field>
        <Field id="q-activity" label="Activité" error={errors.activity} required data-id="element-236" data-mp="mg9eduha8vlw">
          <select id="q-activity" value={values.activity} onChange={e => setField("activity", e.target.value)} className={`${inputBase} ${border("activity")}`} data-id="element-237" data-mp="me6cod6rpe1c">
            <option value="" data-id="element-238" data-mp="w59fbjfci12q">Sélectionner…</option>
            <option data-id="element-239" data-mp="1074eah9t02dc">Marque cosmétique</option>
            <option data-id="element-240" data-mp="10636qmrzxy0d">Laboratoire / façonnier</option>
            <option data-id="element-241" data-mp="156qt2v5a5qo9">Parfumerie</option>
            <option data-id="element-242" data-mp="157she1bb49nx">Distributeur</option>
            <option data-id="element-243" data-mp="154o66jzfcdnr">Autre</option>
          </select>
        </Field>
        <Field id="q-country" label="Pays de livraison" data-id="element-244" data-mp="1a522x5vks16h">
          <select id="q-country" value={values.country} onChange={e => setField("country", e.target.value)} className={`${inputBase} ${border("country")}`} data-id="element-245" data-mp="36ydnl6ods3ph">
            <option data-id="element-246" data-mp="3bz1d1nwyochl">France</option>
            <option data-id="element-247" data-mp="33vzhjk1e48hm">Belgique</option>
            <option data-id="element-248" data-mp="33x0r1k2rcmx8">Suisse</option>
            <option data-id="element-249" data-mp="33y2dslt4yxus">Allemagne</option>
            <option data-id="element-250" data-mp="2zvfho8pfmqrs">Italie</option>
            <option data-id="element-251" data-mp="2zw8heppz4g0f">Espagne</option>
            <option data-id="element-252" data-mp="3duf89lwi9fql">Autre pays</option>
          </select>
        </Field>
      </div>

      <fieldset className="mt-6" data-id="element-253" data-mp="3dsc9acddkc9q">
        <legend className="text-sm font-medium text-ink" data-id="element-254" data-mp="38ussb4palle3">Volume annuel estimé</legend>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-id="element-255" data-mp="3muv72i2azyp4">
          {volumes.map(v => {
          const selected = values.volume === v.id;
          return <button key={v.id} type="button" aria-pressed={selected} onClick={() => setField("volume", selected ? "" : v.id)} className={`h-11 whitespace-nowrap rounded-xl border text-sm transition-colors duration-150 ease-out ${selected ? "border-olive bg-olive text-paper" : "border-line text-olive hover:bg-cream"}`} data-id="element-256" data-mp="3irp3ji6kslmc">
                {v.label}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="mt-6" data-id="element-257" data-mp="t0pdfw5k85um">
        <Field id="q-message" label="Votre projet" error={errors.message} data-id="element-258" data-mp="szwdbe13welg">
          <textarea id="q-message" rows={4} value={values.message} onChange={e => setField("message", e.target.value)} placeholder="Application visée, cahier des charges, certification requise, délai…" className={`w-full resize-y rounded-xl border bg-white px-4 py-3 text-sm text-ink placeholder:text-stone/70 focus:outline-none focus:ring-2 focus:ring-olive/20 ${border("message")}`} data-id="element-259" data-mp="f1po0ma83mxe" />
        </Field>
      </div>

      <div className="mt-6 space-y-3" data-id="element-260" data-mp="1bm5a5ajybcp">
        <label className="flex cursor-pointer items-start gap-3 text-sm text-olive" data-id="element-261" data-mp="3iiy3izhhaaoy">
          <input type="checkbox" checked={values.samples} onChange={e => setField("samples", e.target.checked)} className="mt-0.5 h-4 w-4 accent-olive" data-id="element-262" data-mp="3il11q6ssba4r" />
          Je souhaite recevoir des échantillons
        </label>
        <label className="flex cursor-pointer items-start gap-3 text-sm text-olive" data-id="element-263" data-mp="2t2yn7mfujh7">
          <input id="q-consent" type="checkbox" checked={values.consent} onChange={e => setField("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-olive" aria-describedby={errors.consent ? "q-consent-error" : undefined} data-id="element-264" data-mp="2r8fnyuq91fr" />
          J'accepte d'être recontacté(e) par Domaine de la Rouquette au sujet de cette demande.
        </label>
        {errors.consent && <p id="q-consent-error" className="text-xs text-terracotta" data-id="element-265" data-mp="3f8wvjcnfig7y">{errors.consent}</p>}
      </div>

      {status === "error" && <p className="mt-6 rounded-xl bg-terracotta/10 p-4 text-sm text-terracotta-dark" role="alert" data-id="element-266" data-mp="3b6i9yksdl2cz">
          L'envoi a échoué. Réessayez ou écrivez-nous à pro@domainedelarouquette.fr.
        </p>}

      <button type="submit" disabled={status === "submitting"} className="mt-8 flex h-13 w-full items-center justify-center gap-2 rounded-full bg-terracotta py-4 text-sm font-medium text-paper transition-colors duration-150 ease-out hover:bg-terracotta-dark disabled:cursor-wait disabled:opacity-80" data-id="element-267" data-mp="2wbltxlebrapc">
        {status === "submitting" ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-268" data-mp="2w40ghm08oe53" /> Envoi en cours…
          </> : `Envoyer ma demande${items.length ? ` (${items.length} ingrédient${items.length > 1 ? "s" : ""})` : ""}`}
      </button>
    </form>;
}
interface FieldProps {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}
function Field({
  id,
  label,
  error,
  required,
  children
}: FieldProps) {
  return <div data-id="element-269" data-mp="2i7mwo688g0ng">
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-ink" data-id="element-270" data-mp="2i7ej0c4acrb7">
        {label}
        {required && <span className="text-terracotta" data-id="element-271" data-mp="1kolkakfutqb1"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-terracotta" data-id="element-272" data-mp="1pny7556q6xdm">{error}</p>}
    </div>;
}